/**
 * Move Items Logic
 * Shared logic for moving files and folders via drag-drop or cut-paste
 */

import { Datum, Filetree } from "../datastructure";
import { ActionContext, toApiPath } from "./types";
import moveItemsApi from "@/components/FpAppV4/services/moveItems";
import { isReadOnlyDatum } from "../specialFolders";
import { isPathPrefix, pathsEqual } from "@/utils/pathSegments";

/** Collect a datum and all its loaded descendants (via children links). */
function collectDatumAndDescendants(datum: Datum): Datum[] {
  const result = [datum];
  for (const child of datum.children ?? []) {
    result.push(...collectDatumAndDescendants(child));
  }
  return result;
}

export interface MoveItemsOptions {
  /** Items being moved (Datum objects) */
  items: Datum[];
  /** Target datum (file or folder) */
  target: Datum;
  /** Filetree instance for looking up descendants */
  filetree: Filetree;
  /** Action context for callbacks */
  context: ActionContext;
  /** Optional new name for the item (used for rename operations) */
  newName?: string;
}

/**
 * Move items to a target location
 * - If target is a file, items are placed in the file's parent folder
 * - If target is a folder, items are placed inside that folder
 * - If newName is provided, the item is renamed (only valid for single item)
 */
export async function moveItems({
  items,
  target,
  filetree,
  context,
  newName,
}: MoveItemsOptions): Promise<boolean> {
  const isAcrossDrivesMode = filetree.isAcrossDrivesMode;
  const readOnlyCtx = {
    readOnlyDriveNames: filetree.readOnlyDriveNames,
    sftpDriveNames: filetree.sftpDriveNames,
    sharepointRootNames: filetree.sharepointRootNames,
    isAcrossDrivesMode,
  };
  // Determine target folder
  const targetFolder = target.type === "file" ? target.parent : target;
  if (!targetFolder) {
    console.error("moveItems: Cannot drop on file without parent");
    return false;
  }

  // Prevent moving into read-only special folders
  if (isReadOnlyDatum(targetFolder, readOnlyCtx)) {
    context.showToast?.("Cannot move items into this folder", "error");
    return false;
  }

  // Prevent moving items from read-only special folders
  for (const item of items) {
    if (isReadOnlyDatum(item, readOnlyCtx)) {
      context.showToast?.("Cannot move items from this folder", "error");
      return false;
    }
  }

  // Validation: check all items are from the same drive as target
  // In single-drive mode, root (path []) represents the drive itself,
  // so use context.driveId. Otherwise use the target's payload driveId.
  const targetDriveId =
    !isAcrossDrivesMode && targetFolder.path.length === 0
      ? (context.driveId ?? null)
      : (targetFolder.payload?.driveId ?? null);

  for (const item of items) {
    const itemDriveId = item.payload?.driveId ?? null;
    if (itemDriveId !== targetDriveId) {
      context.showToast?.(
        "Cannot move files between different drives",
        "error"
      );
      return false;
    }
  }

  // Validation: check we're not dropping a folder into itself or a descendant
  const targetPath = targetFolder.path;
  for (const item of items) {
    if (item.type === "folder") {
      if (isPathPrefix(item.path, targetPath)) {
        context.showToast?.(
          "Cannot move a folder into itself or its subfolder",
          "error"
        );
        return false;
      }
    }
  }

  // For rename operations, validate newName
  const isRename = !!newName;
  if (isRename) {
    if (items.length !== 1) {
      console.error("moveItems: newName can only be used with a single item");
      return false;
    }
    if (newName.trim() === "" || newName === items[0].name) {
      return true; // No change needed
    }
  }

  // Filter out items already in target folder (no-op moves) - unless renaming
  const itemsToMove = isRename
    ? items
    : items.filter((item) => {
        return !pathsEqual(item.path.slice(0, -1), targetPath);
      });

  if (itemsToMove.length === 0) {
    return true; // Nothing to move, but not an error
  }

  // Collect all datums being moved (for pending state and rollback)
  const movedDatums = itemsToMove.flatMap(collectDatumAndDescendants);
  const originalPaths = new Map(movedDatums.map((d) => [d, [...d.path]]));

  // Store source paths for API calls BEFORE optimistic update mutates them
  const sourcePaths = new Map(
    itemsToMove.map((item) => [item, [...item.path]])
  );

  // Mark as pending and do optimistic update
  movedDatums.forEach((d) => filetree.pendingMoveDatums.add(d));
  const removedDatums: Datum[] = [];
  itemsToMove.forEach((item, idx) => {
    const removed = filetree.moveDatum(
      item,
      targetFolder.path,
      idx === 0 ? newName : undefined
    );
    removedDatums.push(...removed);
  });

  try {
    // Calculate target folder path for API (strip synthetic drive prefix if needed)
    const apiTargetPath = toApiPath(targetFolder.path, filetree);
    const driveId = targetDriveId || undefined;

    // Separate files and folders for unified API
    const files: Array<{ file_id: string; new_name?: string }> = [];
    const folders: Array<{ path: string[]; new_name?: string }> = [];

    for (let i = 0; i < itemsToMove.length; i++) {
      const item = itemsToMove[i];
      const itemSourcePath = sourcePaths.get(item)!;
      const apiSourcePath = toApiPath(itemSourcePath, filetree);
      // Only first item gets the newName (for rename operations)
      const itemNewName = i === 0 ? newName : undefined;

      if (item.type === "folder") {
        folders.push({ path: apiSourcePath, new_name: itemNewName });
      } else {
        const payload = item.payload;
        if (payload?.type === "file" && payload.fileId) {
          files.push({ file_id: payload.fileId, new_name: itemNewName });
        }
      }
    }

    // Use unified move endpoint for all items in a single transaction
    await moveItemsApi({
      files,
      folders,
      target_path: apiTargetPath,
      drive_id: driveId,
    });

    filetree.pendingMoveDatums.clear();
    filetree.onRebuild?.(itemsToMove[0]);
    if (isRename) {
      context.showToast?.(`Renamed to "${newName}"`, "success");
    } else {
      context.showToast?.(`Moved ${itemsToMove.length} item(s)`, "success");
    }
    return true;
  } catch (error) {
    console.error("moveItems: Failed to move items", error);

    // Rollback: re-add any datums removed during merge, then restore paths
    if (removedDatums.length > 0) {
      filetree.datums.push(...removedDatums);
    }
    for (const [datum, path] of originalPaths) {
      datum.path = path;
    }
    filetree.pendingMoveDatums.clear();
    filetree.rebuildLookup();
    filetree.onRebuild?.(targetFolder);

    context.showToast?.(
      isRename ? "Failed to rename" : "Failed to move items",
      "error"
    );
    return false;
  }
}
